import React, { useState } from "react";

interface TimePickerProps {
  label?: string;
  value?: Date | null;
  onChange?: (date: Date) => void;
  format: "12h" | "24h";
  required?: boolean;
  helpText?: string;
  errorText?: string;
  className?: string;
}

const TimePicker: React.FC<TimePickerProps> = ({
  label,
  value,
  onChange,
  format,
  required,
  helpText,
  errorText,
  className = "",
}) => {
  const getInitialValues = () => {
    if (!value) {
      return {
        hours: format === "12h" ? 12 : 0,
        minutes: 0,
        period: "AM" as "AM" | "PM",
      };
    }

    const hours24 = value.getHours();
    const minutes = Math.floor(value.getMinutes() / 15) * 15;

    if (format === "12h") {
      const hours12 =
        hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24;
      const period = hours24 >= 12 ? "PM" : "AM";
      return { hours: hours12, minutes, period };
    }

    return { hours: hours24, minutes, period: "AM" as "AM" | "PM" };
  };

  const [timeState, setTimeState] = useState(getInitialValues());

  const handleChange = (newState: typeof timeState) => {
    setTimeState(newState);

    const date = value ? new Date(value) : new Date();

    let hours24 = newState.hours;
    if (format === "12h") {
      if (newState.period === "PM" && newState.hours !== 12) {
        hours24 = newState.hours + 12;
      } else if (newState.period === "AM" && newState.hours === 12) {
        hours24 = 0;
      }
    }

    date.setHours(hours24);
    date.setMinutes(newState.minutes);
    date.setSeconds(0);
    date.setMilliseconds(0);

    onChange?.(date);
  };

  const hours =
    format === "12h"
      ? Array.from({ length: 12 }, (_, i) => i + 1)
      : Array.from({ length: 24 }, (_, i) => i);

  const minutes = [0, 15, 30, 45];

  return (
    <div className="mb-3">
      {label && (
        <label className="block text-sm font-medium mb-2 text-gray-700">
          {label}
          {required && <span className="text-danger ml-1">*</span>}
        </label>
      )}
      <div className="flex items-center gap-1">
        {/* Hours dropdown */}
        <select
          value={timeState.hours}
          onChange={(e) =>
            handleChange({ ...timeState, hours: parseInt(e.target.value) })
          }
          className={`p-2 rounded border flex-1 bg-white ${
            errorText ? "border-danger" : "border-gray-300"
          } focus:outline-none focus:ring-2 ${
            errorText ? "focus:ring-danger" : "focus:ring-info"
          } ${className}`}
        >
          {hours.map((h) => (
            <option key={h} value={h}>
              {format === "24h" ? h.toString().padStart(2, "0") : h}
            </option>
          ))}
        </select>

        <span className="text-gray-500">:</span>

        {/* Minutes dropdown */}
        <select
          value={timeState.minutes}
          onChange={(e) =>
            handleChange({ ...timeState, minutes: parseInt(e.target.value) })
          }
          className={`p-2 rounded border flex-1 bg-white mr-1 ${
            errorText ? "border-danger" : "border-gray-300"
          } focus:outline-none focus:ring-2 ${
            errorText ? "focus:ring-danger" : "focus:ring-info"
          } ${className}`}
        >
          {minutes.map((m) => (
            <option key={m} value={m}>
              {m.toString().padStart(2, "0")}
            </option>
          ))}
        </select>

        {/* AM/PM toggle for 12-hour format */}
        {format === "12h" && (
          <div className="flex border border-gray-300 rounded overflow-hidden">
            <button
              type="button"
              onClick={() => handleChange({ ...timeState, period: "AM" })}
              className={`px-3 py-2 text-sm font-medium transition-colors ${
                timeState.period === "AM"
                  ? "bg-info text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              AM
            </button>
            <button
              type="button"
              onClick={() => handleChange({ ...timeState, period: "PM" })}
              className={`px-3 py-2 text-sm font-medium transition-colors ${
                timeState.period === "PM"
                  ? "bg-info text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              PM
            </button>
          </div>
        )}
      </div>

      {helpText && !errorText && (
        <p className="text-xs text-gray-500 mt-1">{helpText}</p>
      )}
      {errorText && <p className="text-xs text-danger mt-1">{errorText}</p>}
    </div>
  );
};

export { TimePicker };
